import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/common/StatusBadge';
import { PlatformIcon } from '../components/common/PlatformIcon';
import { dashboardApi } from '../lib/api';
import {
  Building2, Users, FileText, Send, CalendarDays, AlertTriangle, Image, Plus, ArrowRight,
} from 'lucide-react';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.getStats().then((r) => r.data),
  });

  const stats = [
    { label: 'Companies', value: data?.total_companies ?? 0, icon: Building2, color: 'text-primary' },
    { label: 'Accounts', value: data?.total_accounts ?? 0, icon: Users, color: 'text-blue-500' },
    { label: 'Drafts', value: data?.total_drafts ?? 0, icon: FileText, color: 'text-gray-500' },
    { label: 'Scheduled', value: data?.total_scheduled ?? 0, icon: CalendarDays, color: 'text-sky-500' },
    { label: 'Published', value: data?.total_published ?? 0, icon: Send, color: 'text-emerald-500' },
    { label: 'Failed', value: data?.total_failed ?? 0, icon: AlertTriangle, color: 'text-red-500' },
  ];

  return (
    <AppShell title="Dashboard">
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
          {stats.map((stat) => (
            <Card key={stat.label} className="hover:shadow-md transition-shadow duration-180">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className={`w-4 h-4 ${stat.color}`} />
                </div>
                <div className="text-2xl font-semibold font-display tabular-nums">
                  {isLoading ? <Skeleton className="h-8 w-12" /> : stat.value}
                </div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => navigate('/companies/new')} data-testid="quick-create-company">
            <Plus className="w-4 h-4 mr-2" /> New Company
          </Button>
          <Button variant="secondary" onClick={() => navigate('/companies')} data-testid="quick-view-companies">
            <Building2 className="w-4 h-4 mr-2" /> View Companies
          </Button>
        </div>

        {/* Companies & Recent Posts */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Recent Companies */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-display">Companies</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/companies')}>
                  View all <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : data?.recent_companies?.length > 0 ? (
                <div className="space-y-2">
                  {data.recent_companies.map((company) => (
                    <button
                      key={company._id}
                      onClick={() => navigate(`/companies/${company._id}`)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors duration-150 text-left"
                      data-testid={`company-link-${company.slug}`}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0"
                        style={{ backgroundColor: company.brand_color || '#0d9488' }}
                      >
                        {company.name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{company.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{company.slug}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No companies yet</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Posts */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">Recent Posts</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : data?.recent_posts?.length > 0 ? (
                <div className="space-y-2">
                  {data.recent_posts.slice(0, 5).map((post) => (
                    <div
                      key={post._id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors duration-150"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{post.title || post.caption?.substring(0, 50)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {post.platform_targets?.slice(0, 3).map((p) => (
                            <PlatformIcon key={p} platform={p} size="xs" />
                          ))}
                        </div>
                      </div>
                      <StatusBadge status={post.status} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No posts yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
